import { useEffect, useRef, type CSSProperties } from 'react';
import Select, {
  type MenuPlacement,
  type OptionProps,
  type SingleValue,
  type SingleValueProps,
  type StylesConfig
} from 'react-select';
import { MdPeopleAlt } from 'react-icons/md';
import { useSelector } from 'react-redux';
import { useGetUserInfoQuery } from '@/app/common/api/authApi';
import type { UserInfoResponse } from '@/app/common/api/types/authApiTypes';
import TextLoading from './skeleton/TextLoading';
import { useLocation, useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { setDomain } from '@/app/common/reducers/domainSlice';
import accessLevelApi from '@/app/common/api/accessLevelApi';
import { useAppContext } from '@/Main';
import _ from 'lodash';
import { removeFilters } from '@/app/common/reducers/filterSlice';
import DomainFavicon from '@/app/common/components/DomainFavicon';

type UserDomain = {
  url: string;
  slug: string;
  show_name?: string;
  access_level?: number | string;
};

type DomainOption = {
  label: string;
  value: string;
  show_name?: string;
  access_level?: number | string;
};

type SelectedDomain = {
  name?: string;
  slug?: string;
  show_name?: string;
};

type DomainSelectorState = {
  selectedDomain?: SelectedDomain;
};

type DomainSelectorProps = {
  selectOnly?: boolean;
  value?: string | null;
  onChange?: (domain: {
    slug: string;
    name: string;
    show_name?: string;
  }) => void;
  className?: string;
  menuPlacement?: MenuPlacement;
};

const DomainSelector = ({
  selectOnly = false,
  value = null,
  onChange,
  className,
  menuPlacement
}: DomainSelectorProps) => {
  const {
    config: { showBurgerMenu },
    setConfig
  } = useAppContext();
  const handleNavItemClick = () => {
    if (showBurgerMenu) {
      setConfig('showBurgerMenu', !showBurgerMenu);
    }
  };
  const { data: userInfo, isFetching: isLoading } = useGetUserInfoQuery();
  const selectedDomain = useSelector(
    (state: DomainSelectorState) => state.selectedDomain
  );
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const syncedPathnameRef = useRef<string | null>(null);

  useEffect(() => {
    if (selectOnly) return;
    if (!isLoading && userInfo) {
      const domains = getUserDomains(userInfo);

      // اگر کاربر لاگین‌شده دامنه‌ای نداشت
      if (domains.length === 0) {
        dispatch(
          setDomain({
            name: 'no-domain',
            slug: 'no-domain'
          })
        );
      } else {
        // دامنه ذخیره‌شده (مثلاً از localStorage / اکانت قبلی) فقط وقتی معتبر است
        // که در لیست دامنه‌های کاربر فعلی وجود داشته باشد
        const isSelectedDomainOwnedByUser = domains.some(
          d => d.slug === selectedDomain?.slug
        );

        // اگر دامنه خالی، no-domain، یا متعلق به اکانت دیگری بود → اولین دامنه کاربر فعلی
        if (
          _.isEmpty(selectedDomain) ||
          selectedDomain?.name === 'no-domain' ||
          !isSelectedDomainOwnedByUser
        ) {
          dispatch(
            setDomain({
              name: domains[0]?.url,
              slug: domains[0]?.slug,
              show_name: domains[0]?.show_name
            })
          );
        } else if (!selectedDomain?.show_name) {
          const matched = domains.find(d => d.slug === selectedDomain.slug);
          if (matched?.show_name) {
            dispatch(
              setDomain({
                name: selectedDomain.name,
                slug: selectedDomain.slug,
                show_name: matched.show_name
              })
            );
          }
        }

        dispatch(accessLevelApi.util.invalidateTags(['AccessLevel']));
      }

      // آدرس منبع دامنه است فقط وقتی خود pathname عوض شده (ورود مستقیم یا navigate).
      // تغییر redux قبل از navigate نباید دامنه قبلی را از URL کهنه برگرداند.
      if (syncedPathnameRef.current !== location.pathname) {
        syncedPathnameRef.current = location.pathname;
        const fullPath = location.pathname.split('/');
        const isAiAnalysisDetails =
          location.pathname.includes('/content-tools/ai-analysis/') &&
          !location.pathname.includes('/ai-analysis/history/');
        const domainSlugFromPath = isAiAnalysisDetails
          ? fullPath[4]
          : fullPath[fullPath.length - 2];
        const domainNameFromPath = isAiAnalysisDetails
          ? fullPath[5]
          : fullPath[fullPath.length - 1];
        if (selectedDomain?.slug !== domainSlugFromPath) {
          const matchedDomain = domains.find(d => d.slug === domainSlugFromPath);
          if (matchedDomain) {
            dispatch(
              setDomain({
                name: domainNameFromPath,
                slug: domainSlugFromPath,
                show_name: matchedDomain.show_name
              })
            );
            dispatch(accessLevelApi.util.invalidateTags(['AccessLevel']));
          }
        }
      }
    }
  }, [isLoading, location.pathname, userInfo, selectedDomain?.slug, selectOnly]);

  const handleChangeRouting = (data: SingleValue<DomainOption>) => {
    if (!data) return;

    if (selectOnly) {
      onChange?.({
        slug: data.value,
        name: data.label,
        show_name: data.show_name
      });
      return;
    }

    const basePath = removeLastTwoParts(location.pathname);
    dispatch(removeFilters());
    dispatch(
      setDomain({ name: data.label, slug: data.value, show_name: data.show_name })
    );

    // Backlink tools: keep the current tab while changing the selected domain.
    const isBacklinkPath =
      location.pathname === '/backlink' ||
      location.pathname.startsWith('/backlink/');
    if (isBacklinkPath) {
      const parts = location.pathname.split('/').filter(Boolean);
      const tool = 'backlink';
      const tab = parts[1] || 'overview';
      const params = new URLSearchParams(location.search);
      params.delete('u');
      params.delete('m');
      const query = params.toString();
      const path = `/${tool}/${tab}/${data.value}/${data.label}`;
      navigate(
        `${path}${query ? `?${query}` : ''}`
      );
      handleNavItemClick();
      return;
    }

    if (basePath) {
      if (basePath.includes('crawl')) {
        const partsOfBasePath = basePath.split('/');
        if (partsOfBasePath[partsOfBasePath.length - 1] === 'last') {
          navigate(`${basePath}/${data.value}/${data.label}`);
        } else {
          let newPathBase = partsOfBasePath.slice(0, 2).join('/');
          navigate(`${newPathBase}/history/${data.value}/${data.label}`);
        }
      } else if (basePath.includes('keyword-research')) {
        const partsOfBasePath = basePath.split('/');
        let newPathBase = partsOfBasePath.slice(0, 3).join('/');
        navigate(`${newPathBase}/${data.value}/${data.label}`);
      } else if (basePath.includes('content-tools/ai-analysis')) {
        const parts = location.pathname.split('/');
        const tab = parts[3] || 'history';
        if (tab === 'history') {
          navigate(
            `/content-tools/ai-analysis/history/${data.value}/${data.label}`
          );
        } else {
          navigate(
            `/content-tools/ai-analysis/${tab}/${data.value}/${data.label}/last`
          );
        }
      } else if (
        basePath.includes('content-tools') ||
        basePath.includes('reporting') ||
        basePath.includes('indexer')
      ) {
        const partsOfBasePath = basePath.split('/');
        let newPathBase = partsOfBasePath.slice(0, 3).join('/');
        navigate(`${newPathBase}/${data.value}/${data.label}`);
      } else if (
        basePath.includes('keyword-tracker') ||
        basePath.includes('tracker-ai')
      ) {
        const partsOfBasePath = basePath.split('/');
        let newPathBase = partsOfBasePath.slice(0, 3).join('/');
        navigate(`${newPathBase}/${data.value}/${data.label}`);
      } else if (basePath.includes('serp')) {
        const partsOfBasePath = basePath.split('/');
        // if (partsOfBasePath[partsOfBasePath.length - 1] === 'last') {
        if (!basePath.includes('serp/history')) {
          navigate(
            `${partsOfBasePath.slice(0.3).join('/')}/${data.value}/${data.label}/last/last/last/last`
          );
        } else {
          let newPathBase = partsOfBasePath.slice(0, 2).join('/');
          navigate(`${newPathBase}/history/${data.value}/${data.label}`);
        }
      }
    }
    handleNavItemClick();
  };

  const domains = getUserDomains(userInfo);
  const selectedSlug = selectOnly
    ? value || null
    : Object.keys(selectedDomain || {}).length !== 0
      ? selectedDomain?.slug
      : domains[0]?.slug;

  const selectValue: DomainOption | null = (() => {
    if (!selectedSlug) return null;
    const matched = domains.find(d => d.slug === selectedSlug);
    if (selectOnly) {
      if (!matched) return null;
      return {
        label: matched.url,
        value: matched.slug,
        show_name: matched.show_name,
        access_level: matched.access_level
      };
    }

    const label =
      selectedDomain?.name === 'no-domain'
        ? 'بدون دامنه'
        : Object.keys(selectedDomain || {}).length !== 0
          ? selectedDomain?.name
          : domains[0]?.url;

    return {
      label: label ?? '',
      value: selectedSlug,
      show_name: selectedDomain?.show_name ?? matched?.show_name,
      access_level: matched?.access_level
    };
  })();

  return (
    <div className={className ?? 'px-xl-0 px-3 position-relative'}>
      {userInfo && !isLoading ? (
        userInfo?.data?.domains && (
          <Select<DomainOption, false>
            classNamePrefix="react-select"
            placeholder={
              !isLoading && userInfo ? 'دامنه را انتخاب کنید' : '...'
            }
            isDisabled={!(!isLoading && userInfo)}
            menuShouldScrollIntoView={true}
            id="domain-selector"
            className={selectOnly ? 'rounded-2' : 'rounded-end-2 my-2'}
            menuPlacement={menuPlacement}
            menuPortalTarget={
              selectOnly && typeof document !== 'undefined'
                ? document.body
                : undefined
            }
            styles={domainSelectStyles}
            components={{ Option: OptionLayout, SingleValue: DomainValue }}
            onChange={handleChangeRouting}
            value={selectValue}
            closeMenuOnSelect={true}
            isSearchable={true}
            noOptionsMessage={() => 'دامنه ای یافت نشد!!!'}
            options={domains.map(domain => ({
              label: domain.url,
              value: domain.slug,
              show_name: domain.show_name,
              access_level: domain.access_level
            }))}
          />
        )
      ) : (
        <div style={{ margin: '4px 0px' }}>
          <TextLoading height={38} width={'100%'} />
        </div>
      )}
    </div>
  );
};

export default DomainSelector;

const getUserDomains = (userInfo?: UserInfoResponse): UserDomain[] =>
  (userInfo?.data?.domains ?? []) as UserDomain[];

const domainSelectStyles: StylesConfig<DomainOption, false> = {
  menuPortal: baseStyles => ({ ...baseStyles, zIndex: 1060 }),
  menu: baseStyles => ({
    ...baseStyles,
    position: 'absolute',
    zIndex: 9999,
    width: '100%'
  }),
  control: baseStyles => ({
    ...baseStyles,
    direction: 'ltr',
    paddingLeft: '10px',
    paddingRight: '0',
    color: 'var(--falcon-emphasise) !important',
    boxShadow: 'none !important'
  }),
  input: baseStyles => ({
    ...baseStyles,
    direction: 'ltr',
    color: 'var(--falcon-emphasise) !important',
    boxShadow: 'none !important'
  })
};

const removeLastTwoParts = (originalString: string) => {
  const parts = originalString.split('/');
  const modifiedParts = parts.slice(0, -2);
  return modifiedParts.join('/');
};

const OptionLayout = (props: OptionProps<DomainOption, false>) => {
  const { innerProps, innerRef } = props;
  return (
    <>
      <style>{`
        .customized-searchbox-option:hover{
          background-color:var(--falcon-gray-link-hover-300) !important;
        }
        .customized-searchbox-option{
          background-color:var(--falcon-tertiary-bg) !important;
        }
        `}</style>
      <div
        ref={innerRef}
        {...innerProps}
        style={{
          direction: 'ltr',
          backgroundColor: props.isSelected
            ? 'var(--falcon-gray-link-hover-100)'
            : 'var(--falcon-dropdown-bg-global)'
        }}
        className="p-2 cursor-pointer border border-1 customized-searchbox-option"
      >
        <div className="fs-0 text-900 d-flex justify-content-between">
          <div className="d-flex align-items-center gap-1">
            <DomainFavicon domain={props.data.label} />
            {props.data.label}
          </div>

          {props.data.access_level != 1 && (
            <MdPeopleAlt className="ms-1 fs-0" />
          )}
        </div>
        <div className="text-secondary fs--2" style={{ marginLeft: 20 }}>
          {props.data.show_name}
          {/* <br />
             <div style={{ direction: 'ltr', textAlign: 'right' }}>
              {props.data.ex}
            </div> */}
        </div>
      </div>
      {/* {props.options[props.options.length - 1] !== props.data && (
          <div className="border border-1" style={{ width: '100%', height: 1 }} />
        )} */}
    </>
  );
};

const DomainValue = (props: SingleValueProps<DomainOption, false>) => {
  const { innerProps, getStyles } = props;
  return (
    <>
      <div
        {...innerProps}
        style={{
          ...(getStyles('singleValue', props) as CSSProperties),
          color: 'var(--falcon-body-color)'
        }}
        className="d-flex align-items-center gap-1"
      >
        <DomainFavicon domain={props.data.label} />
        {props.data.label}
        {props.data.show_name ? (
          <span className="fs--2 text-500"> ({props.data.show_name})</span>
        ) : null}
      </div>
    </>
  );
};
